import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useEmployeeSelfService } from '../../../contexts/EmployeeSelfServiceContext';
import { getPunchState } from '../../../utils/attendanceHelpers';

const HomeDashboard = () => {
  const navigate = useNavigate();
  const {
    employee,
    deployment,
    attendanceStats,
    leaves,
    payslips,
    unreadCount,
    todayStatus,
    syncing,
    syncNow,
    lastSyncAt,
  } = useEmployeeSelfService();

  const pendingLeaves = leaves.filter((l) => l.status === 'pending').length;
  const punchState = getPunchState(todayStatus);
  const clockedIn = punchState.isPunchedIn;

  return (
    <div className="space-y-6 pb-20 lg:pb-6">
      <div className="bg-card border border-border rounded-lg p-4 md:p-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-foreground">
              Welcome, {employee?.full_name || 'Employee'}!
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {employee?.position || 'Employee'} • {employee?.department || 'Department'}
            </p>
            {deployment?.site_name && (
              <p className="text-xs text-muted-foreground mt-1">
                Deployed at: {deployment.client_name} — {deployment.site_name}
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span>{lastSyncAt ? `Synced ${lastSyncAt.toLocaleTimeString()}` : 'Not synced'}</span>
            <Button variant="outline" size="sm" onClick={syncNow} disabled={syncing} iconName="RefreshCcw">
              {syncing ? 'Syncing...' : 'Sync'}
            </Button>
          </div>
        </div>
      </div>

      {(punchState.canPunchIn || punchState.canPunchOut) && (
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <p className="font-medium text-foreground">
              {clockedIn ? 'You are clocked in' : 'Ready to start your day?'}
            </p>
            <p className="text-sm text-muted-foreground">
              {clockedIn ? 'Punch out when your session ends' : 'Use selfie punch with location enabled'}
            </p>
            {punchState.punchCount > 0 && (
              <p className="text-xs text-muted-foreground mt-1">
                {punchState.punchCount} punch{punchState.punchCount !== 1 ? 'es' : ''} today
              </p>
            )}
          </div>
          <Button
            onClick={() => navigate(clockedIn ? '/erp/selfie-attendance/punch-out' : '/erp/selfie-attendance/punch-in')}
            iconName="Camera"
          >
            {clockedIn ? 'Punch Out' : 'Punch In'}
          </Button>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Present', value: attendanceStats.present, icon: 'CheckCircle', color: 'text-green-600' },
          { label: 'Absent', value: attendanceStats.absent, icon: 'XCircle', color: 'text-red-600' },
          { label: 'Pending Leave', value: pendingLeaves, icon: 'Calendar', color: 'text-amber-600' },
          { label: 'Notifications', value: unreadCount, icon: 'Bell', color: 'text-blue-600' },
        ].map((card) => (
          <div key={card.label} className="bg-card border border-border rounded-lg p-4">
            <Icon name={card.icon} size={18} className={card.color} />
            <div className="text-2xl font-bold mt-2">{card.value}</div>
            <div className="text-xs text-muted-foreground">{card.label}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-card border border-border rounded-lg p-4">
          <h3 className="font-semibold mb-3">Quick Actions</h3>
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate('/erp/employee-self-service/leave')}>Apply Leave</Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/erp/employee-self-service/attendance-details')}>Attendance</Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/erp/employee-self-service/payslip')}>Payslips ({payslips.length})</Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/erp/employee-self-service/regularization')}>Correction</Button>
          </div>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <h3 className="font-semibold mb-3">Recent Leaves</h3>
          {leaves.slice(0, 3).map((leave) => (
            <div key={leave.id} className="text-sm py-1 border-b border-border last:border-0">
              {leave.leave_type} — <span className="capitalize">{leave.status}</span>
            </div>
          ))}
          {leaves.length === 0 && <p className="text-sm text-muted-foreground">No leave requests yet</p>}
        </div>
      </div>
    </div>
  );
};

export default HomeDashboard;
